import React, { useState, useEffect } from "react";
import { useForm } from "../forms/useForm";
import {
  buildExpenseItemsPayload,
  ExpenseItem,
  ExpenseItemPayload,
  mapExpenseItems,
} from "../../shared/expenseItems";
import ReceiptReviewForm, { ReceiptReviewDraft } from "./ReceiptReviewForm";
import { ExpenseUserOption } from "../../shared/expenseUsersApi";
import { fromDateOnlyString, toDateOnlyString } from "../../shared/dateOnly";
import { validateReceiptFile } from "../../shared/receiptFiles";

interface ExpenseFormValues {
  user_id: string | number;
  vendor: string;
  date: Date | string | null;
  sub_total_amount: string;
  total_amount: string;
  vendor_address: string;
  tax_amount: string;
  expense_type: string | number | null;
  notes: string;
  currency: string;
}

interface ExpenseErrors {
  vendor?: string | null;
  sub_total_amount?: string | null;
  total_amount?: string | null;
  tax_amount?: string | null;
  expense_type?: string | null;
  vendor_address?: string | null;
}

interface InitialExpenseData {
  id?: number;
  business_id?: number;
  user_id?: number;
  vendor?: string;
  vendor_address?: string | null;
  sub_total_amount?: string | number | null;
  total_amount?: string | number | null;
  tax_amount?: string | number | null;
  currency?: string | null;
  date?: string | null;
  receipt_path?: string | null;
  original_filename?: string | null;
  mime_type?: string | null;
  size?: number | null;
  expense_type?: number;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
  user_name?: string;
  receipt_items?: ExpenseItem[];
}

interface UpdatePayload {
  user_id?: number;
  vendor?: string;
  vendor_address?: string;
  sub_total_amount?: number;
  total_amount?: number;
  tax_amount?: number;
  date?: string;
  expense_type?: number;
  notes?: string | null;
  currency?: string;
  items?: ExpenseItemPayload[];
}

interface ManualExpensePayload {
  user_id: number | null;
  vendor: string;
  vendor_address: string | null;
  date: string | null;
  expense_type: number | null;
  sub_total_amount: number | null;
  total_amount: number | null;
  tax_amount: number | null;
  notes: string | null;
  currency: string;
  items: ExpenseItemPayload[];
}

interface ApiResult {
  success: boolean;
  data?: unknown;
}

function messageFromData(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;

  const payload = data as {
    message?: unknown;
    errors?: Record<string, unknown>;
  };
  const firstError = payload.errors
    ? Object.values(payload.errors)
        .flat()
        .find((value) => typeof value === "string")
    : null;
  if (typeof firstError === "string") return firstError;

  return typeof payload.message === "string" && payload.message ? payload.message : null;
}

function errorMessageFromResult(result: ApiResult | undefined, fallback: string) {
  return messageFromData(result?.data) || fallback;
}

function errorMessageFromException(error: unknown, fallback: string) {
  if (!error || typeof error !== "object") return fallback;

  const exception = error as {
    message?: unknown;
    response?: { data?: unknown };
  };
  return (
    messageFromData(exception.response?.data) ||
    (typeof exception.message === "string" ? exception.message : null) ||
    fallback
  );
}

interface UploadExpenseFormProps {
  visible: boolean;
  onClose: () => void;
  onUpdate?: (id: number, payload: UpdatePayload) => Promise<ApiResult>;
  onManualCreate?: (payload: ManualExpensePayload | FormData) => Promise<ApiResult>;
  loadReceiptFile?: (expenseId: number) => Promise<Blob>;
  userOptions?: ExpenseUserOption[];
  userOptionsLoading?: boolean;
  initialData?: InitialExpenseData | null;
}

export default function UploadExpenseForm({
  visible,
  onClose,
  onUpdate,
  onManualCreate,
  loadReceiptFile,
  userOptions = [],
  userOptionsLoading = false,
  initialData = null,
}: UploadExpenseFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [uploading, setUploading] = useState<boolean>(false);
  const [items, setItems] = useState<ExpenseItem[]>([]);
  const [submitError, setSubmitError] = useState("");

  const initialFValues: ExpenseFormValues = {
    user_id: Number(localStorage.getItem("id")) || "",
    vendor: "",
    date: null,
    sub_total_amount: "",
    total_amount: "",
    vendor_address: "",
    tax_amount: "",
    expense_type: 1,
    notes: "",
    currency: "CAD",
  };

  const amountError = (value: string | number, label: string, required = false): string | null => {
    if (value === "" || value === null || value === undefined) {
      return required ? `${label} is required` : null;
    }

    const amount = Number(value);
    return Number.isFinite(amount) && amount >= 0 ? null : `${label} must be a non-negative number`;
  };

  const validate = (fieldValues: Partial<ExpenseFormValues> = values): boolean => {
    const temp: ExpenseErrors = {
      vendor: fieldValues.vendor?.trim() ? null : "Merchant is required",
      expense_type: fieldValues.expense_type ? null : "Expense type is required",
      sub_total_amount: amountError(fieldValues.sub_total_amount ?? "", "Subtotal"),
      tax_amount: amountError(fieldValues.tax_amount ?? "", "Tax"),
      total_amount: amountError(fieldValues.total_amount ?? "", "Total", true),
    };
    setErrors(temp);
    return Object.values(temp).every((x) => x === null);
  };

  const { values, setValues, errors, setErrors, resetForm } = useForm(
    initialFValues,
    false,
    validate
  );

  const typedValues = values as ExpenseFormValues;

  useEffect(() => {
    if (initialData) {
      const mapped: ExpenseFormValues = {
        user_id: initialData.user_id ?? (Number(localStorage.getItem("id")) || ""),
        vendor: initialData.vendor || "",
        date: fromDateOnlyString(initialData.date),
        sub_total_amount: String(initialData.sub_total_amount ?? ""),
        total_amount: String(initialData.total_amount ?? ""),
        vendor_address: initialData.vendor_address || "",
        tax_amount: String(initialData.tax_amount ?? ""),
        expense_type: initialData.expense_type ?? 1,
        notes: initialData.notes || "",
        currency: initialData.currency || "CAD",
      };
      setValues(mapped);
      setItems(mapExpenseItems(initialData.receipt_items || [], initialData.currency || "CAD"));
    } else {
      resetForm();
      setPreviewUrl(null);
      setFile(null);
      setItems([]);
    }
    setSubmitError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setPreviewLoading(false);
      setPreviewError("");
      return () => URL.revokeObjectURL(url);
    }

    if (!initialData?.id || !initialData.receipt_path || !loadReceiptFile) {
      setPreviewUrl(null);
      setPreviewLoading(false);
      setPreviewError("");
      return undefined;
    }

    let active = true;
    let objectUrl: string | null = null;
    setPreviewUrl(null);
    setPreviewLoading(true);
    setPreviewError("");

    loadReceiptFile(initialData.id)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (active) setPreviewError("The receipt preview could not be loaded.");
      })
      .finally(() => {
        if (active) setPreviewLoading(false);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file, initialData?.id, initialData?.receipt_path, loadReceiptFile]);

  if (!visible) return null;

  async function submit() {
    setSubmitError("");
    if (!validate(typedValues)) {
      setSubmitError("Correct the highlighted fields before saving.");
      return;
    }

    // If editing an existing expense, call onUpdate with allowed fields
    if (initialData && initialData.id && onUpdate) {
      const payload: UpdatePayload = {};
      if (typedValues.user_id) payload.user_id = Number(typedValues.user_id);
      if (typedValues.vendor) payload.vendor = typedValues.vendor;
      if (typedValues.vendor_address) payload.vendor_address = typedValues.vendor_address;
      if (typedValues.total_amount) payload.total_amount = Number(typedValues.total_amount);
      if (typedValues.sub_total_amount)
        payload.sub_total_amount = Number(typedValues.sub_total_amount);
      if (typedValues.tax_amount) payload.tax_amount = Number(typedValues.tax_amount);
      const date = toDateOnlyString(typedValues.date);
      if (date) payload.date = date;

      if (typedValues.expense_type) payload.expense_type = Number(typedValues.expense_type);
      payload.notes = typedValues.notes || null;
      payload.currency = typedValues.currency;
      payload.items = buildExpenseItemsPayload(items, typedValues.currency);

      setUploading(true);
      try {
        const res = await onUpdate(initialData.id, payload);
        if (!res?.success) {
          setSubmitError(errorMessageFromResult(res, "Could not save this expense."));
        }
        return res;
      } catch (error) {
        setSubmitError(errorMessageFromException(error, "Could not save this expense."));
      } finally {
        setUploading(false);
      }
    }

    // create flow — always use manual-create API
    if (typeof onManualCreate === "function") {
      setUploading(true);
      try {
        // JSON payload
        const payload: ManualExpensePayload = {
          user_id: typedValues.user_id ? Number(typedValues.user_id) : null,
          vendor: typedValues.vendor,
          vendor_address: typedValues.vendor_address || null,
          date: toDateOnlyString(typedValues.date),
          expense_type: typedValues.expense_type ? Number(typedValues.expense_type) : null,
          sub_total_amount: typedValues.sub_total_amount
            ? Number(typedValues.sub_total_amount)
            : null,
          total_amount: typedValues.total_amount ? Number(typedValues.total_amount) : null,
          tax_amount: typedValues.tax_amount ? Number(typedValues.tax_amount) : null,
          notes: typedValues.notes || null,
          currency: typedValues.currency,
          items: buildExpenseItemsPayload(items, typedValues.currency),
        };
        let submission: ManualExpensePayload | FormData = payload;
        if (file) {
          const formData = new FormData();
          Object.entries(payload).forEach(([key, value]) => {
            if (key === "items") {
              formData.append(key, JSON.stringify(value));
            } else if (value !== null && value !== undefined) {
              formData.append(key, String(value));
            }
          });
          formData.append("file", file);
          submission = formData;
        }

        const res = await onManualCreate(submission);
        if (res?.success) {
          onClose();
        } else {
          setSubmitError(errorMessageFromResult(res, "Could not create this expense."));
        }
        return res;
      } catch (error) {
        setSubmitError(errorMessageFromException(error, "Could not create this expense."));
      } finally {
        setUploading(false);
      }
    }
  }

  const isExistingExpense = Boolean(initialData?.id);
  const reviewDraft: ReceiptReviewDraft = {
    user_id: typedValues.user_id,
    vendor: typedValues.vendor,
    vendor_address: typedValues.vendor_address,
    date: toDateOnlyString(typedValues.date) || "",
    sub_total_amount: typedValues.sub_total_amount,
    tax_amount: typedValues.tax_amount,
    total_amount: typedValues.total_amount,
    currency: typedValues.currency,
    expense_type: typedValues.expense_type ?? "",
    notes: typedValues.notes,
  };

  const handleReceiptFileChange = (nextFile: File) => {
    const fileError = validateReceiptFile(nextFile);
    if (fileError) {
      setFile(null);
      setSubmitError(fileError);
      return;
    }

    setSubmitError("");
    setFile(nextFile);
  };

  return (
    <ReceiptReviewForm
      title={isExistingExpense ? `Review expense #${initialData?.id}` : "Create expense"}
      subtitle={
        isExistingExpense
          ? "Correct anything before saving the expense."
          : "Enter the receipt details before creating the expense."
      }
      previewUrl={previewUrl}
      previewMimeType={file?.type || initialData?.mime_type}
      previewLoading={previewLoading}
      previewError={previewError}
      receiptFileName={file?.name}
      onReceiptFileChange={isExistingExpense ? undefined : handleReceiptFileChange}
      userOptions={userOptions}
      userOptionsLoading={userOptionsLoading}
      draft={reviewDraft}
      items={items}
      busy={uploading}
      error={submitError}
      errors={errors}
      submitLabel={isExistingExpense ? "Save expense" : "Create expense"}
      busyLabel={isExistingExpense ? "Saving…" : "Creating…"}
      onDraftChange={(name, value) => {
        setSubmitError("");
        setValues({ ...typedValues, [name]: value });
      }}
      onItemsChange={setItems}
      onCancel={onClose}
      onSubmit={async () => {
        await submit();
      }}
    />
  );
}
